import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  TutorResolvedConfigDTO,
  TutorQuizConfigDTO,
  getNodeQuizConfig,
  saveNodeQuizConfig,
  deleteNodeQuizConfig,
} from '../../../services/tutorQuizService';

interface Props {
  courseId: number;
  nodeId: number;
}

const QUESTION_TYPE_OPTIONS = [
  { value: 'MULTIPLE_CHOICE', label: 'Multiple Choice' },
  { value: 'TRUE_FALSE', label: 'True / False' },
];

function parseTypes(csv?: string): string[] {
  if (!csv) return ['MULTIPLE_CHOICE'];
  return csv.split(',').map((t) => t.trim()).filter(Boolean);
}

export default function TutorNodeQuizConfigPanel({ courseId, nodeId }: Props) {
  const [resolved, setResolved] = useState<TutorResolvedConfigDTO>({});
  const [draft, setDraft] = useState<TutorQuizConfigDTO>({});
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['MULTIPLE_CHOICE']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const load = () => {
    setLoading(true);
    getNodeQuizConfig(courseId, nodeId)
      .then((data) => {
        setResolved(data);
        setDraft({
          maxQuestions: data.maxQuestions,
          timeLimitMinutes: data.timeLimitMinutes,
          minPassPercent: data.minPassPercent,
          maxRetries: data.maxRetries,
          allowAiGeneration: data.allowAiGeneration,
          allowedQuestionTypes: data.allowedQuestionTypes,
        });
        setSelectedTypes(parseTypes(data.allowedQuestionTypes));
      })
      .catch(() => setSnackbar({ open: true, message: 'Failed to load node config', severity: 'error' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [courseId, nodeId]);

  const handleTypeToggle = (value: string) => {
    setSelectedTypes((prev) => {
      if (prev.includes(value)) {
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== value);
      }
      return [...prev, value];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveNodeQuizConfig(courseId, nodeId, { ...draft, allowedQuestionTypes: selectedTypes.join(',') });
      setSnackbar({ open: true, message: 'Topic config saved', severity: 'success' });
      load();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save topic config', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const result = await deleteNodeQuizConfig(courseId, nodeId);
      if (result.hasCourseConfig === false) {
        setSnackbar({ open: true, message: 'No course-level config set — topic will use system defaults', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Topic override removed — reverted to course config', severity: 'success' });
      }
      load();
    } catch {
      setSnackbar({ open: true, message: 'Failed to reset topic config', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">Topic Quiz Config</Typography>
        {resolved.isOverride
          ? <Chip label="Override active" color="primary" size="small" />
          : <Chip label={`Inherited from ${resolved.source ?? 'SYSTEM'}`} variant="outlined" size="small" />}
      </Box>

      <TextField
        label="Max Questions"
        type="number"
        size="small"
        value={draft.maxQuestions ?? ''}
        onChange={(e) => setDraft({ ...draft, maxQuestions: e.target.value ? Number(e.target.value) : undefined })}
      />
      <TextField
        label="Time Limit (minutes)"
        type="number"
        size="small"
        value={draft.timeLimitMinutes ?? ''}
        onChange={(e) => setDraft({ ...draft, timeLimitMinutes: e.target.value ? Number(e.target.value) : undefined })}
      />
      <TextField
        label="Min Pass %"
        type="number"
        size="small"
        value={draft.minPassPercent ?? ''}
        onChange={(e) => setDraft({ ...draft, minPassPercent: e.target.value ? Number(e.target.value) : undefined })}
      />
      <TextField
        label="Max Retries"
        type="number"
        size="small"
        value={draft.maxRetries ?? ''}
        onChange={(e) => setDraft({ ...draft, maxRetries: e.target.value ? Number(e.target.value) : undefined })}
      />

      <FormControlLabel
        control={
          <Switch
            checked={!!draft.allowAiGeneration}
            onChange={(e) => setDraft({ ...draft, allowAiGeneration: e.target.checked })}
          />
        }
        label="Allow AI Generation"
      />

      <Box>
        <FormLabel component="legend">Allowed Question Types</FormLabel>
        <FormGroup row>
          {QUESTION_TYPE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={selectedTypes.includes(opt.value)}
                  onChange={() => handleTypeToggle(opt.value)}
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Override'}
        </Button>
        {resolved.isOverride && (
          <Button variant="outlined" color="warning" onClick={handleReset} disabled={saving}>
            Reset to Course Defaults
          </Button>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
